import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { formatDateOnly, parseDateOnly } from '@/utils/DateOnlyHelpers'
import {
  ConflictInfo,
  CreatePricingAssumption,
  CreatePricingAssumptionDetail,
  UpdatePricingAssumption,
  UpdatePricingAssumptionDetail,
} from '@repo/backend/crt'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const pricingAssumptionsSearchSchema = z.object({
  keyword: z.string().optional(),
  effectiveDateFrom: z.coerce.date().optional(),
  effectiveDateTo: z.coerce.date().optional(),
  ...datatableSearchSchema.shape,
})

export type PricingAssumptionsSearch = z.infer<typeof pricingAssumptionsSearchSchema>

export type PricingAssumptions = Awaited<ReturnType<typeof getPricingAssumptions>>
export type PricingAssumption = Awaited<ReturnType<typeof getPricingAssumptionById>>
export type PricingAssumptionDetail = PricingAssumption['details'][number]

type UseCreatePricingAssumptionArgs = {
  pricingAssumptionDetails: CreatePricingAssumption
  queries: PricingAssumptionsSearch
  handleOnSuccess: (pricingAssumptionId: number) => void
}

type UseUpdatePricingAssumptionArgs = QueryHandlers & {
  pricingAssumptionId: number
}

type UseDeletePricingAssumptionsArgs = QueryHandlers & {
  pricingAssumptionIds: number[]
  queries: PricingAssumptionsSearch
}

async function getPricingAssumptions(search: PricingAssumptionsSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['pricing-excise']['pricing-assumptions'].$get(
    {
      query: stringifyValues({
        ...search,
        effectiveDateFrom: search.effectiveDateFrom
          ? formatDateOnly(search.effectiveDateFrom)
          : undefined,
        effectiveDateTo: search.effectiveDateTo
          ? formatDateOnly(search.effectiveDateTo)
          : undefined,
      }),
    },
    { init: { signal } },
  )

  const pricingAssumptions = await res.json()
  return {
    ...pricingAssumptions,
    results: pricingAssumptions.results.map((assumption) => ({
      ...assumption,
      effectiveDate: parseDateOnly(assumption.effectiveDate),
    })),
  }
}

type PricingAssumptionDetailsQueries = {
  materialGroupCode?: string[]
  brand?: string[]
  province?: string[]
  unmarkedProvince?: string[]
  channel?: string[]
  sort?: {
    id: string
    desc: boolean
  }
}

async function getPricingAssumptionById(
  id: string,
  queries?: PricingAssumptionDetailsQueries,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['pricing-excise']['pricing-assumptions'][':id'].$get(
    {
      param: { id },
      query: queries ? stringifyValues(queries) : {},
    },
    { init: { signal } },
  )

  const pricingAssumption = await res.json()
  return {
    ...pricingAssumption,
    effectiveDate: parseDateOnly(pricingAssumption.effectiveDate),
    details: pricingAssumption.details.map((detail) => ({
      ...detail,
      priceAdjustment: Number(detail.priceAdjustment),
    })),
  }
}

async function createPricingAssumption(pricingAssumptionDetails: CreatePricingAssumption) {
  const api = getApi()

  const res = await api.crt['pricing-excise']['pricing-assumptions'].$post({
    json: pricingAssumptionDetails,
  })

  const pricingAssumption = await res.json()
  return pricingAssumption
}

async function updatePricingAssumption(
  pricingAssumptionId: number,
  details: UpdatePricingAssumption,
) {
  const api = getApi()

  await api.crt['pricing-excise']['pricing-assumptions'][':id'].$patch({
    param: { id: String(pricingAssumptionId) },
    json: details,
  })
}

async function deletePricingAssumptions(pricingAssumptionIds: number[]) {
  const api = getApi()

  await api.crt['pricing-excise']['pricing-assumptions'].bulk.$delete({
    json: { ids: pricingAssumptionIds },
  })
}

async function createPricingAssumptionDetails(
  pricingAssumptionId: string,
  details: CreatePricingAssumptionDetail[],
) {
  const api = getApi()

  const res = await api.crt['pricing-excise']['pricing-assumptions'][':id'].details.$post({
    param: { id: pricingAssumptionId },
    json: { details },
  })

  const result = await res.json()
  return {
    createdAssumptions: result.createdAssumptions.map(
      (detail: (typeof result.createdAssumptions)[number]) => ({
        ...detail,
        priceAdjustment: Number(detail.priceAdjustment),
      }),
    ),
    failedCreations: result.failedCreations,
  }
}

async function deletePricingAssumptionDetail(pricingAssumptionId: string, detailId: number) {
  const api = getApi()

  await api.crt['pricing-excise']['pricing-assumptions'][':id'].details[':detailId'].$delete({
    param: { id: pricingAssumptionId, detailId: String(detailId) },
  })
}

async function deletePricingAssumptionDetails(pricingAssumptionId: string, detailIds: number[]) {
  const api = getApi()

  await api.crt['pricing-excise']['pricing-assumptions'][':id'].details.bulk.$delete({
    param: { id: pricingAssumptionId },
    json: { ids: detailIds },
  })
}

async function updatePricingAssumptionDetails(
  pricingAssumptionId: string,
  updates: Array<{ id: number; data: { priceAdjustment: number } }>,
) {
  const api = getApi()

  const res = await api.crt['pricing-excise']['pricing-assumptions'][':id'].details.bulk.$patch({
    param: { id: pricingAssumptionId },
    json: { updates },
  })

  const updatedDetails = await res.json()
  return updatedDetails.map((detail: (typeof updatedDetails)[number]) => ({
    ...detail,
    priceAdjustment: Number(detail.priceAdjustment),
  }))
}

async function updatePricingAssumptionDetail(
  pricingAssumptionId: string,
  detailId: number,
  data: UpdatePricingAssumptionDetail,
): Promise<
  { success: true; detail: PricingAssumptionDetail } | { success: false; conflicts: ConflictInfo[] }
> {
  const api = getApi()

  try {
    const res = await api.crt['pricing-excise']['pricing-assumptions'][':id'].details[
      ':detailId'
    ].$put({
      param: { id: pricingAssumptionId, detailId: String(detailId) },
      json: data,
    })

    const detail = await res.json()
    return {
      success: true,
      detail: {
        ...detail,
        priceAdjustment: Number((detail as { priceAdjustment: string }).priceAdjustment),
      } as PricingAssumptionDetail,
    }
  } catch (error) {
    // Hono RPC client throws the parsed body directly for 409 responses
    if (error && typeof error === 'object' && 'conflicts' in error) {
      return { success: false, conflicts: (error as { conflicts: ConflictInfo[] }).conflicts }
    }
    throw error
  }
}

export function usePricingAssumptions(
  queries: PricingAssumptionsSearch,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['pricingAssumptions', queries],
    queryFn: ({ signal }) => getPricingAssumptions(queries, signal),
    enabled: options?.enabled ?? true,
  })
}

export const pricingAssumptionByIdQueryOptions = (
  id: string,
  queries?: PricingAssumptionDetailsQueries,
) => ({
  queryKey: ['pricingAssumptionById', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getPricingAssumptionById(id, queries, signal),
})

export function usePricingAssumptionById(id: string, queries?: PricingAssumptionDetailsQueries) {
  return useQuery({
    ...pricingAssumptionByIdQueryOptions(id, queries),
    refetchOnWindowFocus: false,
  })
}

export function useCreatePricingAssumption({
  pricingAssumptionDetails,
  queries,
  handleOnSuccess,
}: UseCreatePricingAssumptionArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['pricingAssumptions', queries],
    mutationFn: () => createPricingAssumption(pricingAssumptionDetails),
    onSuccess: (pricingAssumption) => {
      handleOnSuccess(pricingAssumption.id)

      queryClient.invalidateQueries({
        queryKey: ['pricingAssumptions', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('pricingAssumptions.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.pricingAssumptions.createFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdatePricingAssumption({
  pricingAssumptionId,
  handleOnSuccess,
}: UseUpdatePricingAssumptionArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updatePricingAssumption', pricingAssumptionId],
    mutationFn: (details: UpdatePricingAssumption) =>
      updatePricingAssumption(pricingAssumptionId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['pricingAssumptionById', String(pricingAssumptionId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('pricingAssumptions.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.pricingAssumptions.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeletePricingAssumptions({
  pricingAssumptionIds,
  queries,
  handleOnSuccess,
}: UseDeletePricingAssumptionsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deletePricingAssumptions'],
    mutationFn: () => deletePricingAssumptions(pricingAssumptionIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['pricingAssumptions', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('pricingAssumptions.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.pricingAssumptions.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useCreatePricingAssumptionDetails(pricingAssumptionId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['createPricingAssumptionDetails', pricingAssumptionId],
    mutationFn: (details: CreatePricingAssumptionDetail[]) =>
      createPricingAssumptionDetails(pricingAssumptionId, details),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ['pricingAssumptionById', pricingAssumptionId],
      })

      if (result.failedCreations.length > 0) {
        toast.warning(t('toast.warning', { ns: 'common' }), {
          description: t('pricingAssumptionDetails.partialSuccess', {
            ns: 'crt',
            created: result.createdAssumptions.length,
            failed: result.failedCreations.length,
          }),
          className: toastClassName,
        })
        return
      }

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('pricingAssumptionDetails.createSuccess', {
          ns: 'crt',
          count: result.createdAssumptions.length,
        }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.pricingAssumptionDetails.createFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeletePricingAssumptionDetail(pricingAssumptionId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deletePricingAssumptionDetail', pricingAssumptionId],
    mutationFn: (detailId: number) => deletePricingAssumptionDetail(pricingAssumptionId, detailId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['pricingAssumptionById', pricingAssumptionId],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('pricingAssumptionDetails.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.pricingAssumptionDetails.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeletePricingAssumptionDetails(pricingAssumptionId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deletePricingAssumptionDetails', pricingAssumptionId],
    mutationFn: (detailIds: number[]) =>
      deletePricingAssumptionDetails(pricingAssumptionId, detailIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['pricingAssumptionById', pricingAssumptionId],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('pricingAssumptionDetails.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.pricingAssumptionDetails.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdatePricingAssumptionDetails(pricingAssumptionId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updatePricingAssumptionDetails', pricingAssumptionId],
    mutationFn: (updates: Array<{ id: number; data: { priceAdjustment: number } }>) =>
      updatePricingAssumptionDetails(pricingAssumptionId, updates),
    onSuccess: (updatedDetails) => {
      queryClient.invalidateQueries({
        queryKey: ['pricingAssumptionById', pricingAssumptionId],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('pricingAssumptionDetails.updateSuccess', {
          ns: 'crt',
          count: updatedDetails.length,
        }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.pricingAssumptionDetails.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdatePricingAssumptionDetail(pricingAssumptionId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updatePricingAssumptionDetail', pricingAssumptionId],
    mutationFn: ({ detailId, data }: { detailId: number; data: UpdatePricingAssumptionDetail }) =>
      updatePricingAssumptionDetail(pricingAssumptionId, detailId, data),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({
          queryKey: ['pricingAssumptionById', pricingAssumptionId],
        })

        toast.success(t('toast.success', { ns: 'common' }), {
          description: t('pricingAssumptionDetails.updateSuccess', {
            ns: 'crt',
            count: 1,
          }),
          className: toastClassName,
        })
      }
    },
    onError: (error: unknown) => {
      // This should only be called for actual errors, not conflicts
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.pricingAssumptionDetails.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
